import { NoPhotography } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Theme,
  Typography
} from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import {
  ClusterShipperThirdPartyConversionDeliveryDto,
  clusterShipperThirdPartyConversionDeliveryValidationSchema
} from 'dtos/clusterShipperThirdPartyConversionDeliveryDto'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'

interface ClusterShippersThirdPartyConversionDeliveryAddEditDialogProps {
  initialValues: ClusterShipperThirdPartyConversionDeliveryDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: ClusterShipperThirdPartyConversionDeliveryDto) => void
  open: boolean
  clusterShipperGuid: string
}

function getBackgroundColor(theme: Theme, hasImage: boolean): string {
  return hasImage ? String(theme.palette.success.main) : String(theme.palette.error.main)
}

export default function ClusterShippersThirdPartConversionDeliveryAddEditDialog({
  initialValues,
  clusterShipperGuid,
  isLoading,
  onClose,
  onSave,
  open
}: ClusterShippersThirdPartyConversionDeliveryAddEditDialogProps) {
  const [src, setSrc] = useState<string>('')
  const [format, setFormat] = useState<string>('')

  useEffect(() => {
    setSrc('')
    if (initialValues.clusterShipperThirdPartyConversionDeliveryPhotoFileName && clusterShipperGuid) {
      const fileName = initialValues.clusterShipperThirdPartyConversionDeliveryPhotoFileName
      setFormat(fileName.split('.').pop() ?? '')

      controllers
        .getClusterShipperThirdPartyConversionDeliveryPhotoBase64(clusterShipperGuid, fileName)
        .then(base64 => {
          setSrc(base64)
        })
        .catch(() => setSrc(''))
    } else {
      setSrc('')
    }
  }, [initialValues.clusterShipperThirdPartyConversionDeliveryPhotoFileName, clusterShipperGuid])

  return (
    <Dialog open={open} maxWidth='sm' fullWidth>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={clusterShipperThirdPartyConversionDeliveryValidationSchema}
      >
        {({ errors, handleSubmit, isSubmitting, setFieldValue, values }) => {
          const displaySrc = values.clusterShipperThirdPartyConversionDeliveryPhotoFile
            ? URL.createObjectURL(values.clusterShipperThirdPartyConversionDeliveryPhotoFile)
            : src

          const hasImage = Boolean(displaySrc)

          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Upload/Replace 3rd Party Conversion Delivery Photo</DialogTitle>

                  <Grid item xs={12}>
                    {/* <img
                      alt='3rd Party Conversion Delivery'
                      src={
                        values.clusterShipperThirdPartyConversionDeliveryPhotoFile
                          ? URL.createObjectURL(values.clusterShipperThirdPartyConversionDeliveryPhotoFile)
                          : src
                      }
                      style={{ width: '100%' }}
                    /> */}

                    <Card
                      sx={theme => ({
                        backgroundColor: getBackgroundColor(theme, hasImage),
                        color: '#fff',
                        p: 1,
                        mb: 1,
                        maxWidth: '100%',
                        height: 300
                      })}
                    >
                      <CardContent
                        sx={{
                          display: 'flex',
                          aligntItems: 'center',
                          justifyContent: 'center',
                          height: '100%'
                        }}
                      >
                        {!hasImage && <NoPhotography fontSize='large' sx={{ height: 200 }} />}

                        {hasImage && (
                          <img
                            alt='3rd Party Conversion Delivery'
                            src={`${
                              values.clusterShipperThirdPartyConversionDeliveryPhotoFile
                                ? displaySrc
                                : `data:image/${format};base64,${displaySrc}`
                            }`}
                            style={{
                              maxHeight: '100%',
                              maxWidth: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <input
                      accept='image/png, image/jpeg'
                      hidden
                      type='file'
                      id='clusterShipperThirdPartyConversionDeliveryPhotoFile'
                      onChange={e => {
                        if (e.target.files && e.target.files.length > 0) {
                          const currentDate = new Date()
                          const formattedDate = currentDate.toISOString().split('T')[0]

                          setFieldValue('clusterShipperThirdPartyConversionDeliveryPhotoFile', e.target.files[0])
                          setFieldValue('dropOffDate', formattedDate)
                        }
                      }}
                    />

                    <Button
                      disabled={isLoading || isSubmitting}
                      onClick={() => {
                        document.getElementById('clusterShipperThirdPartyConversionDeliveryPhotoFile')?.click()
                      }}
                      variant='contained'
                    >
                      UPLOAD NEW IMAGE
                    </Button>

                    <Typography color='#808080' display='inline' variant='body2' sx={{ ml: 1 }}>
                      {values.clusterShipperThirdPartyConversionDeliveryPhotoFile?.name ??
                        values.clusterShipperThirdPartyConversionDeliveryPhotoFileName ??
                        'No file uploaded'}
                    </Typography>

                    {errors.clusterShipperThirdPartyConversionDeliveryPhotoFile && (
                      <Typography color='error' variant='body2'>
                        {errors.clusterShipperThirdPartyConversionDeliveryPhotoFile}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Upload New Image Button:**

Pressing the Upload New Image button will allow the user to upload a new 3rd Party Conversion Delivery Photo
and associate it with the Cluster Shipper.

Any existing 3rd Party Conversion Delivery Photo will be overwritten by the new uploaded image. The newly
uploaded image will be linked to the Cluster Shipper and all vehicles associated with that Cluster Shipper.
`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={isLoading || isSubmitting} variant='text' color='inherit' onClick={onClose}>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
